import React, { useState } from 'react';
import {
  ArrowPathIcon,
  CalendarIcon,
  CloudArrowUpIcon,
  ClockIcon,
  CogIcon,
  ServerIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface BackupConfig {
  id: string;
  type: string;
  frequency: string;
  time: string;
  retention: number;
  status: 'active' | 'inactive';
  lastRun?: string;
  nextRun?: string;
}

interface AutomationRule {
  id: string;
  name: string;
  condition: string;
  action: string;
  status: 'active' | 'inactive';
}

export const ConfigureBackupPage: React.FC = () => {
  const [backupConfigs, setBackupConfigs] = useState<BackupConfig[]>([
    {
      id: '1',
      type: 'Full Backup',
      frequency: 'daily',
      time: '00:00',
      retention: 30,
      status: 'active',
      lastRun: '2024-03-20 00:00:00',
      nextRun: '2024-03-21 00:00:00'
    },
    {
      id: '2',
      type: 'Incremental',
      frequency: 'hourly',
      time: '00:30',
      retention: 7,
      status: 'active',
      lastRun: '2024-03-20 12:30:00',
      nextRun: '2024-03-20 13:30:00'
    }
  ]);

  const [automationRules, setAutomationRules] = useState<AutomationRule[]>([
    {
      id: '1',
      name: 'Auto-Recovery on Failure',
      condition: 'On backup failure',
      action: 'Retry backup after 5 minutes',
      status: 'active'
    },
    {
      id: '2',
      name: 'Storage Cleanup',
      condition: 'When storage > 90%',
      action: 'Remove backups older than retention period',
      status: 'active'
    }
  ]);

  const [newBackupConfig, setNewBackupConfig] = useState<Partial<BackupConfig>>({
    type: 'Full Backup',
    frequency: 'daily',
    time: '00:00',
    retention: 30,
    status: 'active'
  });

  const handleToggleStatus = (id: string, type: 'backup' | 'automation') => {
    if (type === 'backup') {
      setBackupConfigs(configs =>
        configs.map(config =>
          config.id === id
            ? { ...config, status: config.status === 'active' ? 'inactive' : 'active' }
            : config
        )
      );
    } else {
      setAutomationRules(rules =>
        rules.map(rule =>
          rule.id === id
            ? { ...rule, status: rule.status === 'active' ? 'inactive' : 'active' }
            : rule
        )
      );
    }
  };

  const handleAddBackupConfig = () => {
    if (newBackupConfig.type && newBackupConfig.frequency && newBackupConfig.time) {
      setBackupConfigs([
        ...backupConfigs,
        {
          id: Date.now().toString(),
          type: newBackupConfig.type,
          frequency: newBackupConfig.frequency,
          time: newBackupConfig.time,
          retention: newBackupConfig.retention || 30,
          status: newBackupConfig.status || 'active'
        }
      ]);
      setNewBackupConfig({
        type: 'Full Backup',
        frequency: 'daily',
        time: '00:00',
        retention: 30,
        status: 'active'
      });
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <ArrowPathIcon className="w-8 h-8 text-[#FF5722] mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Backup Configuration</h1>
        </div>

        {/* Add New Backup Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <CloudArrowUpIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Add New Backup Configuration</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Backup Type</label>
              <select
                value={newBackupConfig.type}
                onChange={(e) => setNewBackupConfig({ ...newBackupConfig, type: e.target.value })}
                className="w-full border rounded-md p-2"
              >
                <option value="Full Backup">Full Backup</option>
                <option value="Incremental">Incremental</option>
                <option value="Differential">Differential</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
              <select
                value={newBackupConfig.frequency}
                onChange={(e) => setNewBackupConfig({ ...newBackupConfig, frequency: e.target.value })}
                className="w-full border rounded-md p-2"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
              <input
                type="time"
                value={newBackupConfig.time}
                onChange={(e) => setNewBackupConfig({ ...newBackupConfig, time: e.target.value })}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Retention (days)</label>
              <input
                type="number"
                value={newBackupConfig.retention}
                onChange={(e) => setNewBackupConfig({ ...newBackupConfig, retention: parseInt(e.target.value) })}
                className="w-full border rounded-md p-2"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleAddBackupConfig}
                className="w-full bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
              >
                Add Configuration
              </button>
            </div>
          </div>
        </div>

        {/* Existing Backup Configurations */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ServerIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Existing Backup Configurations</h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retention</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {backupConfigs.map((config) => (
                  <tr key={config.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.frequency}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.time}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.retention} days</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.lastRun}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.nextRun}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        config.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {config.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(config.id, 'backup')}
                        className={`text-sm ${
                          config.status === 'active' ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {config.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Automation Rules */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <CogIcon className="w-6 h-6 text-[#FF5722] mr-2" />
              <h2 className="text-lg font-bold text-gray-900">Automation Rules</h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condition</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {automationRules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.condition}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rule.action}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        rule.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {rule.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(rule.id, 'automation')}
                        className={`text-sm ${
                          rule.status === 'active' ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {rule.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureBackupPage; 